// Post detail route. Deep-link target for notifications and shareable
// post URLs.
//
// Chrome is rendered from first paint through fully loaded: the header
// card (author identity) and the post body card swap from skeleton stand-ins
// to real content as each query slice resolves, never gating behind a
// fullscreen loading return. Skeleton sub-components are co-located below
// — no parallel `*Skeleton.tsx` file.

import { createFileRoute, notFound } from '@tanstack/react-router';
import { RouterLinkAs } from '@/lib/router-link';
import { useQuery } from '@tanstack/react-query';
import { isUuid } from '@leanin/shared';
import { asUserId, type UserId } from '@leanin/shared/ids';
import {
  Avatar,
  Caption,
  Card,
  DelayedSkeleton,
  PageHeader,
  PersonRowSkeleton,
  Row,
  Skeleton,
  SkeletonText,
  Stack,
  useDelayedLoading,
} from '@leanin/ui';
import { DetailPage } from '@/components/page-shell/index';
import { PostCard } from '@/components/feed/PostCard';
import { PostEntityCardSkeleton } from '@/components/feed/PostEntityCard.skeleton';
import type { FeedPost } from '@/components/feed/types';
import { formatRelativeShort } from '@/lib/format-date';
import { feedKeys } from '@/lib/queries/feed';
import { identityKeys } from '@/lib/queries/identity';
import { isNotFoundApiError } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';

export const Route = createFileRoute('/_authed/posts/$id')({
  beforeLoad: ({ params }) => {
    if (!isUuid(params.id)) throw notFound();
  },
  component: PostDetailPage,
});

function formatAuthorName(profile: {
  firstName?: string;
  lastName?: string;
}): string {
  const first = profile.firstName?.trim() ?? '';
  const last = profile.lastName?.trim() ?? '';
  const composed = `${first} ${last}`.trim();
  return composed.length > 0 ? composed : 'Lean In member';
}

function formatAuthorRole(
  jobTitle: string | null | undefined,
  company: string | null | undefined,
): string {
  const t = jobTitle?.trim() ?? '';
  const c = company?.trim() ?? '';
  if (t && c) return `${t} · ${c}`;
  return t || c;
}

function PostDetailPage() {
  const { id } = Route.useParams();

  const postQuery = useQuery({
    ...feedKeys.detail(id),
    retry: false,
  });

  const authorUserId =
    postQuery.data?.authorUserId !== undefined
      ? asUserId(postQuery.data.authorUserId)
      : null;
  // `identityKeys.member` needs a concrete UserId; the `enabled` gate keeps the
  // query dormant until the post (and thus the author id) resolves, so the
  // placeholder id is never fetched with.
  const authorQuery = useQuery({
    ...identityKeys.member(authorUserId ?? ('' as UserId)),
    enabled: authorUserId !== null,
  });

  const post = postQuery.data ?? null;
  const author = authorQuery.data ?? null;
  const showHeaderSkeleton = useDelayedLoading(post === null || author === null);

  // Post fetch returns `null` on 404; author fetch throws NotFoundApiError.
  // Either way, the post is permanently unreachable — render the branded
  // 404 directly (render-context `throw notFound()` lands in TanStack's
  // catch-boundary instead of its 404 surface in this version).
  // Placed AFTER all hooks so the hook count stays stable across renders.
  if (
    isNotFoundApiError(postQuery.error) ||
    isNotFoundApiError(authorQuery.error) ||
    (postQuery.data === null && !postQuery.isLoading)
  ) {
    return <NotFoundPage />;
  }
  if (postQuery.isError) throw notFound();

  const feedPost: FeedPost | null =
    post !== null && author !== null
      ? {
          id: post.id,
          author: {
            id: post.authorUserId,
            name: formatAuthorName(author),
            role: formatAuthorRole(author.jobTitle, author.company),
            ...(author.photoUrl != null ? { photo: author.photoUrl } : {}),
          },
          time: formatRelativeShort(post.createdAt),
          body: post.body,
          mentions: post.mentions ?? [],
          reactionCounts: post.reactionCounts,
          viewerReaction: post.viewerReaction,
          viewerSaved: post.viewerSaved,
          comments: post.commentCount,
          attachedEntities: post.attachedEntities,
          media: post.media as FeedPost['media'],
          isGlobal: post.isGlobal,
          scopes: post.scopes,
        }
      : null;

  const header =
    feedPost !== null && post !== null ? (
      <PageHeader
        leading={
          <Avatar
            src={author?.photoUrl ?? undefined}
            name={feedPost.author.name}
            size={72}
            href={`/members/${feedPost.author.id}`}
            linkAs={RouterLinkAs}
          />
        }
        title={feedPost.author.name}
        {...(feedPost.author.role ? { subtitle: feedPost.author.role } : {})}
        meta={
          <Caption>
            Posted {feedPost.time}
          </Caption>
        }
      />
    ) : showHeaderSkeleton ? (
      <PageHeader
        leading={<Skeleton width={72} height={72} rounded="full" />}
        title={<Skeleton width={220} height={32} rounded="sm" />}
        preview={<Skeleton width={260} height={24} rounded="sm" />}
        meta={<Skeleton width={120} height={18} rounded="sm" />}
      />
    ) : null;

  return (
    <DetailPage header={header}>
      <DelayedSkeleton
        isLoading={feedPost === null}
        fallback={<PostBodySkeleton />}
      >
        {feedPost !== null ? <PostCard post={feedPost} /> : null}
      </DelayedSkeleton>
    </DetailPage>
  );
}

// Body skeleton for the PostCard. Co-located rather than parallel-filed —
// mirrors PostCard's structure (MemberRow, scope pills, body text, attached
// entity, PostActions footer) so the loading and loaded layouts share
// rhythm.
function PostBodySkeleton() {
  return (
    <Card pad="flush" style={{ overflow: 'hidden' }}>
      <Stack gap="lg" p="xl">
        <PersonRowSkeleton avatarSize={44} />
        <Row gap="sm" align="center">
          <Skeleton width={88} height={24} rounded="full" />
          <Skeleton width={104} height={24} rounded="full" />
        </Row>
        <SkeletonText lines={5} size="md" />
        <PostEntityCardSkeleton />
      </Stack>
      <Row
        py="md"
        px="xl"
        align="center"
        justify="space-between"
        style={{ borderTop: '1px solid var(--border)' }}
      >
        <Row gap="sm" align="center">
          <Skeleton width={60} height={28} rounded="md" />
          <Skeleton width={96} height={28} rounded="md" />
        </Row>
        <Row gap="xs" align="center">
          <Skeleton width={28} height={28} rounded="md" />
          <Skeleton width={28} height={28} rounded="md" />
        </Row>
      </Row>
    </Card>
  );
}
